import { closestIndex } from "../utils/dom";

export interface BulkAction {
	label: string,
	action: string,
	className?: string,
}

export interface TableProps<R,C> {

	cols?: C[],
	items?: R[];

	render?: {
		colHeader?: (c: C) => any,
		colTitle?: (c: C) => any,

		row?: (r: R) => any,
		getRowAttrs?: (r: R) => any,

		headerRow?: () => any,

		colValue?: (r: R, c: C) => any,
	},

	disabled?: boolean,
	interactive?: boolean,
	selectedIndex?: number,

	noItemsMesssage?: any,

	style?: any,
	className?: any,

	onClick?: (e: any, r: R) => void,

	bulkActions?: BulkAction[],
	onBulkAction?: (action: string) => void,

}


export function Table<R,C>(props: TableProps<R,C>) {

	const {
		cols, items, render,
	} = props;

	if (!items || items.length === 0) {
		if (props.noItemsMesssage === undefined || (props.noItemsMesssage && props.noItemsMesssage.length > 0)) {
			return (
				<div key="no-items">{props.noItemsMesssage !== undefined ? props.noItemsMesssage : "There are no items to display"}</div>
			);	
		} else {
			return null;
		}
	}

	const header = 
		(render && render.headerRow) ? render.headerRow()
		: defaultHeaderRow()
	;

	function defaultHeaderRow() {
		const headerCols = [];
		if (cols) {
			let i = 0;
			for(let c of cols) {
				const col = c as any;
	
				const renderCell = ((render && render.colTitle) ? render.colTitle : col.title);
	
				headerCols.push(
					(render && render.colHeader) ? render.colHeader(c) :
						<th
							key={i}
							className={"cell " + col.className}
							data-index={i}
							style={{width: (col.widthPercent ? (col.widthPercent + "%") : undefined)}}
						>
							{(typeof renderCell === "function") ? renderCell(c) : renderCell}
						</th>
				);
				i++;
			}
		}
		return (
			<thead><tr>{headerCols}</tr></thead>
		);
	}

	const rows = [];
	if (items) {		
		let i = 0;
		for(let r of items) {
			if (render && render.row) {
				rows.push(render.row(r));
				continue;
			}

			// build tds
			const tds = [] as any[];
			if (cols) {
				let j = 0;
				for(let c of cols) {
					const col = c as any;

					const renderCell = col.value ? col.value : ((render && render.colValue) ? render.colValue : undefined);

					tds.push(
						<td className={"cell " + (col.className ? col.className : "")} data-index={j} key={j++}>
							{renderCell ? renderCell(r, c) : undefined}
						</td>
					)	
				}
			}

			let attrs;
			if (render && render.getRowAttrs) {
				attrs = render.getRowAttrs(r);
			}

			rows.push(
				<tr
					data-index={i}
					key={i++}
					className={
						"row " + (i%2 ? "alt " : "")
						+ ((props.selectedIndex !== undefined && props.selectedIndex === i) ? "selected " : "")
						+ ((attrs && attrs.className) ? attrs.className : "")
					}
				>
					{tds}
				</tr>
			)


		}
	}

	const bulkActionButtons = props.bulkActions && props.bulkActions.length > 0 && props.onBulkAction ? (
		<div className="table-bulk-actions gap-2">
			{props.bulkActions.map((ba) => (
				<button
					key={ba.action}
					className={ba.className || "flat"}
					onClick={() => props.onBulkAction!(ba.action)}
				>
					{ba.label}
				</button>
			))}
		</div>
	) : null;

	return (
		<>
			<table
				className={
					[
						"table",
						props.disabled ? "disabled" : "",
						props.interactive ? "interactive" : "",
						props.className,
					].join(" ")
				}
				style={props.style}
				onClick={(e) => {
					const index = closestIndex(e.target, ".row");
					if (index < 0) return;

					const r = items ? items[index] : undefined;
					if (r && props.onClick) props.onClick(e, r);
				}}
			>
				{header}
				<tbody>{rows}</tbody>
			</table>
			{bulkActionButtons}
		</>
	);

}